<script setup>
import { computed, ref, watch } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  steps: { type: Array, required: true },
  defaultCollapsed: { type: Boolean, default: false },
});

const isExpanded = ref(!props.defaultCollapsed);
const openDetails = ref({});

watch(
  () => props.defaultCollapsed,
  collapsed => {
    if (collapsed) isExpanded.value = false;
  }
);

const doneCount = computed(
  () =>
    props.steps.filter(step => step.kind === 'tool' && step.status === 'done')
      .length
);

const keyFor = (step, index) => step.callId || `note-${index}`;

const iconFor = step => {
  if (step.kind === 'note') return 'i-lucide-message-square-text';
  if (step.kind === 'approval') {
    if (step.decision === 'approved') return 'i-lucide-shield-check';
    if (step.decision === 'rejected') return 'i-lucide-ban';
    return 'i-lucide-shield-alert';
  }
  if (step.status === 'running') return 'i-lucide-loader-circle';
  if (step.status === 'error') return 'i-lucide-circle-x';
  return 'i-lucide-circle-check';
};

const classFor = step => {
  if (step.kind === 'approval') {
    if (step.decision === 'approved') return 'text-n-teal-11';
    if (step.decision === 'rejected') return 'text-n-ruby-11';
    return 'text-n-amber-11';
  }
  if (step.kind === 'note') return 'text-n-slate-10';
  if (step.status === 'running') return 'text-n-blue-11 animate-spin';
  if (step.status === 'error') return 'text-n-ruby-11';
  return 'text-n-teal-11';
};

const sourceLabel = step => {
  if (!step.server || step.server === 'copilot') return 'Helix';
  return `MCP ${step.server}`;
};

const sourceClass = step => {
  if (!step.server || step.server === 'copilot')
    return 'bg-n-slate-3 text-n-slate-11';
  if (step.classification === 'write') return 'bg-n-amber-3 text-n-amber-11';
  return 'bg-n-blue-3 text-n-blue-11';
};

const statusSuffix = step => {
  const parts = [];
  if (step.durationMs) parts.push(`${Math.round(step.durationMs / 100) / 10}s`);
  if (step.kind === 'approval' && !step.decision)
    parts.push('aguardando autorização');
  else if (step.decision === 'rejected') parts.push('recusado');
  else if (step.decision === 'approved') parts.push('autorizado');
  return parts.join(' · ');
};

const statusClass = step => {
  if (step.kind === 'approval' && !step.decision) return 'text-n-amber-11';
  if (step.decision === 'rejected') return 'text-n-ruby-11';
  if (step.decision === 'approved') return 'text-n-teal-11';
  return 'text-n-slate-10';
};

const prettyArgs = args => {
  if (!args) return '';
  if (typeof args === 'string') return args;
  try {
    return JSON.stringify(args, null, 2);
  } catch (e) {
    return String(args);
  }
};

const toggleDetails = key => {
  openDetails.value = { ...openDetails.value, [key]: !openDetails.value[key] };
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="flex flex-col gap-2">
    <button
      class="group flex items-center gap-2 text-xs text-n-slate-10 hover:text-n-slate-11 transition-colors duration-200 -ml-3"
      @click="isExpanded = !isExpanded"
    >
      <Icon
        :icon="isExpanded ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
        class="w-4 h-4 transition-transform duration-200 group-hover:scale-110"
      />
      <span class="flex items-center gap-2">
        Etapas
        <span
          class="inline-flex items-center justify-center h-4 min-w-4 px-1 text-xs font-medium rounded-full bg-n-solid-3 text-n-slate-11"
        >
          {{ doneCount }}/{{ steps.length }}
        </span>
      </span>
    </button>

    <div v-show="isExpanded" class="space-y-1.5">
      <div
        v-for="(step, index) in steps"
        :key="keyFor(step, index)"
        class="rounded-lg border border-n-weak bg-n-background/50"
      >
        <div
          v-if="step.kind === 'note'"
          class="flex items-start gap-2 p-2 text-xs text-n-slate-11 whitespace-pre-wrap break-words"
        >
          <Icon
            icon="i-lucide-message-square-text"
            class="w-4 h-4 mt-0.5 flex-shrink-0 text-n-slate-10"
          />
          <span>{{ step.text }}</span>
        </div>

        <template v-else>
          <button
            class="w-full flex items-start gap-2 p-2 text-left"
            @click="toggleDetails(keyFor(step, index))"
          >
            <Icon
              :icon="iconFor(step)"
              class="w-4 h-4 mt-0.5 flex-shrink-0"
              :class="classFor(step)"
            />
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 min-w-0">
                <span class="text-sm text-n-slate-12 truncate">
                  {{ step.label }}
                </span>
              </div>
              <div class="flex flex-wrap items-center gap-1.5 mt-0.5">
                <span
                  class="inline-flex items-center gap-1 px-1.5 h-4 rounded text-[10px] font-medium uppercase tracking-wide"
                  :class="sourceClass(step)"
                >
                  <Icon icon="i-lucide-plug" class="w-3 h-3" />
                  {{ sourceLabel(step) }}
                </span>
                <span class="text-xs text-n-slate-10 truncate font-mono">
                  {{ step.name }}
                </span>
                <span
                  v-if="statusSuffix(step)"
                  class="text-xs"
                  :class="statusClass(step)"
                >
                  · {{ statusSuffix(step) }}
                </span>
              </div>
            </div>
            <Icon
              :icon="
                openDetails[keyFor(step, index)]
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-chevron-down'
              "
              class="w-4 h-4 mt-0.5 flex-shrink-0 text-n-slate-9"
            />
          </button>

          <div
            v-if="openDetails[keyFor(step, index)]"
            class="px-2 pb-2 space-y-1.5"
          >
            <div class="text-[10px] uppercase tracking-wide text-n-slate-10">
              Chamada
            </div>
            <pre
              class="text-xs bg-n-solid-2 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all text-n-slate-11 max-h-40"
              >{{ prettyArgs(step.args) || '(sem argumentos)' }}</pre
            >
            <template v-if="step.preview">
              <div class="text-[10px] uppercase tracking-wide text-n-slate-10">
                Retorno
              </div>
              <pre
                class="text-xs bg-n-solid-2 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all text-n-slate-11 max-h-48"
                >{{ step.preview }}</pre
              >
            </template>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
